import { Injectable, Renderer2, RendererFactory2 } from "@angular/core";
import { GoldIds, GOLDS } from "@soposopo/sopo-models/dist/shop/Golds";
import { DynamicContentService } from "../dynamic-content/dynamic-content.service";
import { SmartAudioService } from "../smart-audio/smart-audio.service";

@Injectable({
    providedIn: 'root'
})
export class PurchaseAnimationService {
    private renderer: Renderer2;
    private readonly _animationLinetType: AnimationLineType = 'straight';
    goldArray: Array<[string, typeof GOLDS[GoldIds]]>;

    constructor(
        private renderFactory: RendererFactory2,
        private dynamicContentService: DynamicContentService,
        private smartAudioService: SmartAudioService,
    ) {
        this.renderer = renderFactory.createRenderer(null, null);
    }

    async animation(productId: GoldIds, product: typeof GOLDS[GoldIds], animationToPlay: ShopAnimationPartToPlay) {
        let backdrop: HTMLElement;
        try {
            backdrop = this.createBackdrop();
            await this.dynamicContentService.openPurchaseProduct(productId, product, animationToPlay);
            const productElement = document.getElementById(`animation-product-${productId}`);
            await this.appearAnimation(productElement);

            await this.sleep(1000);

            if (animationToPlay === 'gold' || animationToPlay === 'all' || animationToPlay === 'shop-gold-only') {
                await this.animateGolds(productId, animationToPlay, backdrop);
            }

            if (productId !== 'gold_1k' && (animationToPlay === 'sweepbuck' || animationToPlay === 'all')) {
                await this.animateSweepbuck(productId, animationToPlay, backdrop);
            }

            await this.sleep(1250);

            await this.disappearAnimation(productElement);

        } catch (error) {
            console.error(error)
        } finally {
            this.dynamicContentService.closePurchaseProduct();
            if (backdrop) backdrop.remove();
        }
    }

    async animateGolds(productId: GoldIds, playAnimation: ShopAnimationPartToPlay, appendGold: HTMLElement) {
        const gold = document.getElementById(`gold-${productId}`);
        await this.goldAnimation(gold, appendGold, playAnimation);
        await this.sleep(500);
    }

    async animateSweepbuck(productId: GoldIds, playAnimation: ShopAnimationPartToPlay, appendSweepsbucks: HTMLElement) {
        const sweepsbuck = document.getElementById(`sweepsbuck-${productId}`);
        await this.sweepsbuckAnimation(sweepsbuck, appendSweepsbucks, playAnimation);
    }

    async goldAnimation(gold: HTMLElement, appendGold: HTMLElement, playAnimation: ShopAnimationPartToPlay) {
        const headerGold = document.getElementById((playAnimation == 'all' || playAnimation == 'shop-gold-only') ? 'homepage-gold' : 'gold-chip-counter');

        if(!gold || !appendGold || !headerGold) return;

        this.smartAudioService.playSounds('gold-purchase');
        const sourceGoldRect = gold.getBoundingClientRect();

        const goldArray: HTMLImageElement[] = [];
        for (let i = 0; i < 5; i++) {
            goldArray.push(
                this.setElement(
                    appendGold,
                    sourceGoldRect.width,
                    sourceGoldRect.height,
                    sourceGoldRect.left,
                    sourceGoldRect.top,
                    '/assets/icon/chips/gold_chip.webp',
                    'gold',
                    6-i,
                )
            );
        }

        if (this._animationLinetType === 'straight') {
            const translate = this.calculateTranslate(goldArray[0], headerGold, 'gold');
            await this.playStraightAnimation(translate, goldArray, 'gold');
        } else {
            const values = this.calculateTargetProperties(headerGold);
            await this.playCurvedAnimation(values, goldArray, 'gold');
        }
    }

    async sweepsbuckAnimation(sweepsbuck: HTMLElement, appendSweepsbucks: HTMLElement, playAnimation: ShopAnimationPartToPlay) {
        const headerSweepsbuck = document.getElementById(playAnimation == 'sweepbuck' ? 'sweepstakes-sweepbuck-img' : 'homepage-sweepbuck');

        if (!sweepsbuck || !appendSweepsbucks || !headerSweepsbuck) return;

        this.smartAudioService.playSounds('sweepsbuck-purchase');

        const sourceSweepsbuckRect = sweepsbuck.getBoundingClientRect();

        const transformValue = getComputedStyle(sweepsbuck).getPropertyValue("transform");
        let angle = 0;
        if (transformValue && transformValue !== 'none') {
            const values = transformValue.match(/matrix\(([^)]+)\)/);
            if (values) {
                const [a, b] = values[1].split(',').map(Number);
                angle = Math.round(Math.atan2(b, a) * (180 / Math.PI));
            }
        }
        const scaleFactor = this.calculateScaleFactor(sweepsbuck.offsetWidth, sweepsbuck.offsetHeight, sourceSweepsbuckRect.width, angle);
        const width = sweepsbuck.offsetWidth * scaleFactor;
        const height = sweepsbuck.offsetHeight * scaleFactor;
        const left = sourceSweepsbuckRect.left + sourceSweepsbuckRect.width / 2;
        const top = sourceSweepsbuckRect.top + sourceSweepsbuckRect.height / 2;

        const sweepbucksArray: HTMLImageElement[] = [];
        for (let i = 0; i < 5; i++) {
            sweepbucksArray.push(
                this.setElement(
                    appendSweepsbucks,
                    width,
                    height,
                    left,
                    top,
                    '/assets/img/sweep/money.webp',
                    'sweepsbuck',
                    6-i,
                    angle,
                )
            );
        }

        if (this._animationLinetType === 'straight') {
            const translate = this.calculateTranslate(sweepbucksArray[0], headerSweepsbuck, 'sweepsbuck');
            await this.playStraightAnimation(translate, sweepbucksArray, 'sweepsbuck');
        } else {
            const targetProperties = this.calculateTargetProperties(headerSweepsbuck);
            await this.playCurvedAnimation(targetProperties, sweepbucksArray, 'sweepsbuck');
        }
    }

    async playStraightAnimation(target: string, array: HTMLImageElement[], animationType: ShopAnimationType) {
        for (let img of array) {
            img.style.transform = `${target}`;
            img.style.transition = `transform 0.3s cubic-bezier(.22,.34,.9,.88), filter 0.01s`;
            if (animationType === 'sweepsbuck') {
                img.style.filter = 'drop-shadow(0px 0px 15px #BDFF00';
            }
            await this.sleep(75);
            this.sleep(250).then(() => img.remove());
        }
    }

    async playCurvedAnimation(targetProperties: ITargetProperties, imagesToAnimate: HTMLImageElement[], animationType: ShopAnimationType) {
        for (let img of imagesToAnimate) {
            img.style.width = `${targetProperties.width}px`;
            img.style.height = `${targetProperties.height}px`;
            img.style.top = `${targetProperties.top}px`;
            img.style.left = `${targetProperties.left}px`;

            img.style.transition = `top 0.3s, left 0.3s ease-in-out, width 0.3s, height 0.3s, transform 0.3s, filter 0.01s`;
            if (animationType === 'sweepsbuck') {
                img.style.filter = 'drop-shadow(0px 0px 15px #BDFF00';
                img.style.transform = '';
            }
            await this.sleep(75);
            this.sleep(250).then(() => img.remove());
        }
    }

    async appearAnimation(productElement: HTMLElement) {
        const widthAndHeight = this.appearsDependingOnDisplay();
        productElement.style.transition = 'transform 0.5s cubic-bezier(.22,.34,.9,.88), opacity 0.5s';
        await this.sleep(200);
        productElement.style.width = widthAndHeight.width;
        productElement.style.height = widthAndHeight.height;
        productElement.style.zIndex = '1';
        productElement.style.transform = 'translate(-50%, -50%) scale(1)';
        productElement.style.opacity = '1';
    }

    async disappearAnimation(productElement: HTMLElement) {
        productElement.style.transform = 'translate(-50%, -50%) scale(0)';
        productElement.style.transition = 'transform 0.6s, opacity 0.6s'
        await this.sleep(200);
        productElement.style.opacity = '0';
        await this.sleep(600)
    }

    private appearsDependingOnDisplay() {
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;
        if (windowHeight < 500)
            return { width: '10vw', height: '10vw' };
        if (windowWidth > 300 && windowHeight < 668)
            return { width: '20vw', height: '20vw' };
        if (windowWidth > 300 && windowHeight < 742)
            return { width: '15vw', height: '15vw' };
        return { width: '20vw', height: '20vw' };
    }

    private setElement(appendInContainer: HTMLElement, width: number, height: number, left: number, top: number, image: string, animationType: ShopAnimationType, zIndex: number, angle = 0) {
        const element = this.renderer.createElement('img') as HTMLImageElement;
        this.renderer.setProperty(element, 'src', image);
        this.renderer.setStyle(element, 'position', 'absolute');
        this.renderer.setStyle(element, 'width', `${width}px`);
        this.renderer.setStyle(element, 'height', `${height}px`);
        this.renderer.setStyle(element, 'z-index', `${zIndex}`);
        if (animationType === 'gold') {
            this.renderer.setStyle(element, 'left', `${left}px`);
            this.renderer.setStyle(element, 'top', `${top}px`);
        } else {
            this.renderer.setStyle(element, 'left', `${left}px`);
            this.renderer.setStyle(element, 'top', `${top}px`);
            this.renderer.setStyle(element, 'transform', `translate(-50%, -50%)${angle ? `rotate(${angle}deg)` : ''}`);
        }
        this.renderer.appendChild(appendInContainer, element);
        return element;
    }

    private calculateTranslate(source: HTMLElement, target: HTMLElement, animationType: ShopAnimationType): string {
        const sourceClientRect = source.getBoundingClientRect();
        const targetClientRect = target.getBoundingClientRect();
        const translateX = (targetClientRect.x + targetClientRect.width / 2) - (sourceClientRect.x + sourceClientRect.width / 2);
        const translateY = (targetClientRect.y + targetClientRect.height / 2) - (sourceClientRect.y + sourceClientRect.height / 2);
        const targetSourceScaleFactor = targetClientRect.width / sourceClientRect.width;

        const translate = `translate(${translateX}px, ${translateY}px) scale(${targetSourceScaleFactor})`;

        if (animationType === 'gold') return translate;

        return 'translate(-50%, -50%) ' + translate;
    }

    private calculateTargetProperties(target: HTMLElement): ITargetProperties {
        const targetClientRect = target.getBoundingClientRect();

        return {
            width: targetClientRect.width,
            height: targetClientRect.height,
            top: targetClientRect.top,
            left: targetClientRect.left,
        }
    }

    private sleep(duration: number) {
        return new Promise<void>(resolve => setTimeout(resolve, duration));
    }

    private createBackdrop() {
        const sopoAppElement = document.getElementById('sopo-app');
        const backdrop = this.renderer.createElement('div');
        this.renderer.setStyle(backdrop, 'height', '100%');
        this.renderer.setStyle(backdrop, 'width', '100%');
        this.renderer.setStyle(backdrop, 'position', 'fixed');
        this.renderer.setStyle(backdrop, 'z-index', 12);
        this.renderer.appendChild(sopoAppElement, backdrop);
        return backdrop;
    }

    private calculateScaleFactor(originalWidth: number, originalHeight: number, rotatedWidth: number, rotationAngle: number): number {
        const angleRad = rotationAngle * Math.PI / 180;
        const denom = Math.abs(originalWidth * Math.cos(angleRad)) + Math.abs(originalHeight * Math.sin(angleRad));
        return rotatedWidth / denom;
    }
}

export type ShopAnimationPartToPlay = 'gold' | 'sweepbuck' | 'all' | 'shop-gold-only';

type ShopAnimationType = 'gold' | 'sweepsbuck';

type AnimationLineType = 'straight' | 'curved';

interface ITargetProperties {
    width: number,
    height: number,
    top: number,
    left: number
};
